import { useSyncExternalStore } from 'react';

export type PanelBridgeData = {
	contentId: string | undefined;
};

export type PanelDataBridge<TData extends PanelBridgeData> = {
	publish: (data: TData) => void;
	clear: () => void;
	subscribe: (callback: () => void) => () => void;
	useValue: (contentId: string | undefined) => TData;
	clearForTesting: () => void;
};

/**
 * Builds an ephemeral client-side bridge carrying data the connections byline has
 * already collected, so the Teamwork Graph preview panel can render it.
 *
 * A bridge is needed because the byline and the panel are in different subtrees:
 * the panel is rendered by `PreviewPanelContainer` as a sibling of the page content
 * in `PageLayout`, so it sees neither the byline's props nor the providers the
 * byline collects from. The preview-panels API also treats a repeat `open()` with
 * the same panel id as a no-op, so its `content` closure can never be handed fresh
 * props either. Publishing is what keeps the panel live.
 *
 * Each call owns its own state, so bridges whose producers resolve at different
 * times cannot clobber one another.
 *
 * `initialData` must be a module constant: it is returned as-is on a `contentId`
 * miss and as the server snapshot, and `useSyncExternalStore` re-renders forever if
 * that identity changes between calls.
 */
export const createPanelDataBridge = <TData extends PanelBridgeData>(
	initialData: TData,
): PanelDataBridge<TData> => {
	let current: TData = initialData;
	const subscribers = new Set<() => void>();

	const notify = (): void => {
		subscribers.forEach((callback) => {
			callback();
		});
	};

	const publish = (data: TData): void => {
		current = data;
		notify();
	};

	const clear = (): void => {
		current = initialData;
		notify();
	};

	const subscribe = (callback: () => void): (() => void) => {
		subscribers.add(callback);

		return () => {
			subscribers.delete(callback);
		};
	};

	// Returns the stored object rather than building a new one, so
	// `useSyncExternalStore` sees a stable snapshot between publishes.
	const getSnapshot = (): TData => current;

	const getServerSnapshot = (): TData => initialData;

	/**
	 * Reads the published data, but only when it belongs to `contentId`. The filter
	 * lives here rather than in each consumer so a new section cannot forget it and
	 * silently render the previous page's data after an SPA transition.
	 */
	const useValue = (contentId: string | undefined): TData => {
		const data = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

		return data.contentId === contentId ? data : initialData;
	};

	const clearForTesting = (): void => {
		current = initialData;
		subscribers.clear();
	};

	return { publish, clear, subscribe, useValue, clearForTesting };
};
